import React, { createContext, useState, useEffect, useContext } from 'react';
import { BACKEND_URL } from '../config';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Helper function to handle fetch errors
const handleFetchWithRetry = async (url, options = {}, retries = 3) => {
  try {
    console.log(`Attempting to fetch: ${url}`);
    
    // Add mobile-friendly timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    // Try HTTP first
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      mode: 'cors', // Explicitly set CORS mode
      cache: 'no-cache' // Don't use cached responses
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    console.error("Fetch error:", error);
    
    // Check if the error is due to a timeout
    if (error.name === 'AbortError') {
      console.log("Request timed out, retrying...");
      if (retries > 1) {
        return handleFetchWithRetry(url, options, retries - 1);
      }
      throw new Error("Server request timed out. Please check your network connection.");
    }
    
    // If HTTP fails and we're using HTTP, try HTTPS
    if (url.startsWith('http:') && retries > 0) {
      const httpsUrl = url.replace('http:', 'https:');
      console.log("Retrying with HTTPS:", httpsUrl);
      
      try {
        return await fetch(httpsUrl, options);
      } catch (httpsError) {
        console.error("HTTPS attempt failed:", httpsError);
      }
    }
    
    // If still failing and we have retries left
    if (retries > 1) {
      console.log(`Retrying... ${retries - 1} attempts left`);
      return handleFetchWithRetry(url, options, retries - 1);
    }
    
    throw error; // Re-throw the last error
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('unknown'); // 'online', 'offline', 'unknown'

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network is online');
      setNetworkStatus('online');
    };
    
    const handleOffline = () => {
      console.log('Network is offline');
      setNetworkStatus('offline');
      setError('नेटवर्क कनेक्शन नहीं है। Network connection lost.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Set initial status
    setNetworkStatus(navigator.onLine ? 'online' : 'offline');
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load user on initial render if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await handleFetchWithRetry(`${BACKEND_URL}/api/auth/user`, {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          });

          if (!response.ok) {
            throw new Error('Failed to load user');
          }

          const userData = await response.json();
          setUser(userData);
        } catch (err) {
          console.error('Error loading user:', err.message);
          logout(); // Clear token if it's invalid
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Register user
  const register = async (userData) => {
    setError(null);
    try {
      const response = await handleFetchWithRetry(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Set token and user data
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Login user
  const login = async (credentials) => {
    setError(null);
    
    // Check network status before attempting login
    if (networkStatus === 'offline') {
      setError('नेटवर्क कनेक्शन नहीं है। Network connection is offline.');
      throw new Error('Network connection is offline');
    }
    
    try {
      const response = await handleFetchWithRetry(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Set token and user data
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      const errorMessage = err.message === 'Failed to fetch' 
        ? 'सर्वर से कनेक्ट नहीं हो सका। Could not connect to server. Check your network connection.'
        : err.message;
        
      setError(errorMessage);
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Change password
  const changePassword = async (passwordData) => {
    setError(null);
    try {
      const response = await handleFetchWithRetry(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(passwordData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Admin reset user password
  const resetUserPassword = async (resetData) => {
    setError(null);
    try {
      const response = await handleFetchWithRetry(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(resetData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Check if user has access to a boat
  const hasBoatAccess = (boatId) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.assignedBoats.includes(boatId);
  };

  // Check server connection
  const checkServerConnection = async () => {
    try {
      // Try the root endpoint first
      const response = await handleFetchWithRetry(`${BACKEND_URL}/`, {
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        console.log("Server connection successful!");
        return true;
      }
      
      // If root fails, try the health endpoint
      const healthResponse = await handleFetchWithRetry(`${BACKEND_URL}/health`, {
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (healthResponse.ok) {
        console.log("Server health check successful!");
        return true;
      }
      
      console.error("Server responded but with an error status.");
      return false;
    } catch (err) {
      console.error("Server check completely failed:", err);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      token,
      user,
      loading,
      error,
      networkStatus,
      isAdmin,
      hasBoatAccess,
      register,
      login,
      logout,
      changePassword,
      resetUserPassword,
      checkServerConnection
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 